import { Injectable } from '@angular/core';
import { SizeFilterHandler } from './filter-handlers/size-filter-handler';
import { StateFilterHandler } from './filter-handlers/state-filter-handler';
import { RatingsFilterHandler } from './filter-handlers/ratings-filter-handler';
import { TaxStatusFilterHandler } from './filter-handlers/tax-status-filter-handler';
import { TypeFilterHandler } from './filter-handlers/type-filter-handler';
import { SectorFilterHandler } from './filter-handlers/sector-filter-handler';
import { PurposeFilterHandler } from './filter-handlers/purpose-filter-handler';
import { DealStructureFilterHandler } from './filter-handlers/deal-structure-filter-handler';
import { ManagerFilterHandler } from './filter-handlers/manager-filter-handler';
import { DateFilterHandler } from './filter-handlers/date-filter-handler';
import { SyndicateAdvancedFilterHandler } from './filter-handlers/syndicate-advanced-filter-handler';
import { FinancialAdvisorAdvancedFilterHandler } from './filter-handlers/financial-advisor-advanced-filter-handler';
import { ObligorAdvancedFilterHandler } from './filter-handlers/obligor-advanced-filter-handler';
import { DefaultFilterHandler } from './filter-handlers/default-filter-handler';
import { IssueTypeFilterHandler } from './filter-handlers/issue-type-filter-handler';
import { ERPFilterHandler } from './filter-handlers/erp-filter-handler';
import { BankQualifiedFilterHandler } from './filter-handlers/bank-qualified-filter-handler';
import { RopFilterHandler } from './filter-handlers/rop-filter-handler';
import { SourceOfRepaymentFilterHandler } from './filter-handlers/source-of-repayment-filter-handler';
import { DateDateFilterHandler } from './filter-handlers/dated-date-filter-handler';
import { FirstCouponDateFilterHandler } from './filter-handlers/first-coupon-date-filter-handler';
import { FirstCallFilterHandler } from './filter-handlers/first-call-date-filter-handler';
import { CouponAdvancedFilterHandler } from './filter-handlers/coupon-advanced-filter-handler';
import { OfferingTypeAdvancedFilterHandler } from './filter-handlers/offering-type-advanced-filter-handler';
import { PriceAdvancedFilterHandler } from './filter-handlers/price-advanced-filter-handler';
import { MaturityYearFilterHandler } from './filter-handlers/maturity-year-filter-handler';
import { ESGFilterHandler } from './filter-handlers/esg-filter-handler';
import {
  IFilterHandler,
  ConfigureOptionsParams,
  FilterOptions,
  GridFilterUnionModel,
  GridFiltersMap,
  AppliedFilterItem
} from './filter-types';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../state/calendar-filter/state';
import { DescriptionFilterHandler } from './filter-handlers/description-filter-handler';
import { QIBFilterHandler } from './filter-handlers/qib-filter-handler';
import { MonitorFilter } from '../../state/monitor-filters/state';
import { FilterLabelPipe, FilterValuePipe } from '../../pipes';
import { CalendarGridColumnName } from '../../grid/types/column.types';
import { SoleManagedFilterHandler } from './filter-handlers/sole-managed-filter-handler';
import { PricingStatusFilterHandler } from './filter-handlers/pricing-status-filter-handler';
import { ForwardDeliveryFilterHandler } from './filter-handlers/forward-delivery-filter-handler';
import { CombinedDocumentsFilterHandler } from './filter-handlers/combined-documents-filter-handler';
import { FilterChange, FilterClear } from '../../state/calendar-filter/actions';
import { BidSubmittedFilterHandler } from './filter-handlers/bid-submitted-filter-handler';
import { SubordinateAdvancedFilterHandler } from './filter-handlers/subordinate-advanced-filter-handler';
import { IssuerFilterHandler } from './filter-handlers/issuer-filter-handler';
import { ParityPlusFilterHandler } from './filter-handlers/parity-plus-filter-handler';

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  private handlers: { [key: string]: IFilterHandler } = {};

  private configurationHandlerList: IFilterHandler[] = [];

  private appliedFiltersOverrides: string[];

  private filterLabel: FilterLabelPipe;
  private filterValue: FilterValuePipe;

  constructor() {
    this.initFilterHandlers();
    this.filterLabel = new FilterLabelPipe();
    this.filterValue = new FilterValuePipe();
  }

  public getFilterHandler(filter: string | CalendarFilter): IFilterHandler {
    const filterName: string =
      filter instanceof CalendarFilter
        ? (filter as CalendarFilter).name
        : filter;

    const handler: IFilterHandler = Object.prototype.hasOwnProperty.call(
      this.handlers,
      filterName
    )
      ? this.handlers[filterName]
      : null;

    if (!handler) {
      return new DefaultFilterHandler();
    }
    return handler;
  }

  private initFilterHandlers() {
    this.configurationHandlerList = [
      new DateFilterHandler(),
      new SizeFilterHandler(),
      new StateFilterHandler(),
      new RatingsFilterHandler(),
      new TaxStatusFilterHandler(),
      new TypeFilterHandler(),
      new SectorFilterHandler(),
      new DealStructureFilterHandler(),
      new PurposeFilterHandler(),
      new ManagerFilterHandler(),
      new DescriptionFilterHandler(),
      new IssueTypeFilterHandler(),
      new ERPFilterHandler(),
      new BankQualifiedFilterHandler(),
      new ParityPlusFilterHandler(),
      new RopFilterHandler(),
      new SourceOfRepaymentFilterHandler(),
      new SyndicateAdvancedFilterHandler(),
      new ObligorAdvancedFilterHandler(),
      new SubordinateAdvancedFilterHandler(),
      new FinancialAdvisorAdvancedFilterHandler(),
      new CouponAdvancedFilterHandler(),
      new OfferingTypeAdvancedFilterHandler(),
      new PriceAdvancedFilterHandler(),
      new MaturityYearFilterHandler(),
      new FirstCallFilterHandler(),
      new FirstCouponDateFilterHandler(),
      new DateDateFilterHandler(),
      new QIBFilterHandler(),
      new ESGFilterHandler(),
      new SoleManagedFilterHandler(),
      new PricingStatusFilterHandler(),
      new ForwardDeliveryFilterHandler(),
      new CombinedDocumentsFilterHandler(),
      new BidSubmittedFilterHandler(),
      new IssuerFilterHandler()
    ].concat(this.getChildHandlers());
    this.handlers = this.createHandlersObject(this.configurationHandlerList);
    this.appliedFiltersOverrides = [
      CalendarGridColumnName.Ratings,
      CalendarGridColumnName.ESG,
      CalendarGridColumnName.Documents
    ];
  }

  // To be overridden in child classes to add handlers during init
  protected getChildHandlers(): IFilterHandler[] {
    return [];
  }

  public getHandlersForFiltering(
    filters: CalendarFiltersState
  ): IFilterHandler[] {
    const filterNames = filters ? Object.keys(filters) : [];
    return filterNames
      ? filterNames
        .map((filterName) => this.handlers[filterName])
        .filter((val) => !!val)
      : [];
  }

  getAppliedFiltersItems(
    filters: CalendarFilter<any>[] | MonitorFilter[]
  ): AppliedFilterItem[] {
    let result: AppliedFilterItem[] = [];
    if (filters && filters.length) {
      for (let i = 0; i < filters.length; i++) {
        const filter: CalendarFilter = filters[i] as CalendarFilter;
        if (this.appliedFiltersOverrides.indexOf(filter.name) !== -1) {
          const handler: IFilterHandler = this.getFilterHandler(filter.name);
          const items = handler.getAppliedFiltersItems(filter);
          result = [...result, ...items];
        } else {
          result.push(this.getAppliedFilterItemFromFilter(filter));
        }
      }
    }
    return result;
  }

  filterClear(
    filterClear: FilterClear,
    state: CalendarFiltersState
  ): FilterChange {
    let result = { name: filterClear.name, value: undefined };
    if (
      filterClear.name &&
      this.appliedFiltersOverrides.indexOf(filterClear.name) !== -1 &&
      filterClear.subFilterName !== 'Ratings'
    ) {
      const handler: IFilterHandler = this.getFilterHandler(filterClear.name);
      result = handler.filterClear(filterClear, state);
    }
    return result;
  }

  private getAppliedFilterItemFromFilter(
    filter: CalendarFilter<any> | MonitorFilter
  ): AppliedFilterItem {
    return {
      name: filter.name,
      label: filter.label,
      filterLabel: this.filterLabel.transform(filter),
      filterValue: this.filterValue.transform(filter)
    };
  }

  public recalculateExclusiveValues(
    filters: CalendarFiltersState,
    filterOptions: FilterOptions
  ): CalendarFiltersState {
    if (filters) {
      Object.keys(filters).forEach((key: string) => {
        const filterHandler: IFilterHandler = this.getFilterHandler(key);
        const currentFilter: CalendarFilter = filters[key];
        if (currentFilter && currentFilter.model) {
          filterHandler.recalculateExclusiveValues(
            currentFilter,
            filterOptions
          );
        }
      });
    }
    return filters;
  }

  public toStoreFilterModel(
    [key, value]: [string, GridFilterUnionModel],
    filterOptions: FilterOptions
  ): GridFilterUnionModel {
    if (value) {
      const filterHandler: IFilterHandler = this.getFilterHandler(key);
      return filterHandler.toStoreFilterModel(value, filterOptions);
    }
    return value;
  }

  public toGridFilterModel(
    filters: Partial<CalendarFiltersState>,
    filterOptions: FilterOptions
  ): GridFiltersMap {
    if (filters) {
      return Object.keys(filters).reduce((acc, key) => {
        const currentFilter = filters[key];
        const filterHandler: IFilterHandler = this.getFilterHandler(
          currentFilter.name
        );
        const model = filterHandler.toGridFilterModel(
          currentFilter,
          filterOptions
        );
        acc[key] = model;
        return acc;
      }, {}) as GridFiltersMap;
    } else {
      return {} as GridFiltersMap;
    }
  }

  public configureFilterOptions(
    params: ConfigureOptionsParams,
    handlerList: IFilterHandler[] = this.configurationHandlerList
  ) {
    handlerList.forEach((handler) => handler.configureFilterOptions(params));
  }

  createHandlersObject(
    configurationHandlerList: IFilterHandler[]
  ): { [key: string]: IFilterHandler } {
    const handlers: { [key: string]: IFilterHandler } = {};
    configurationHandlerList.forEach((handler) => {
      handlers[handler.name] = handler;
    });
    return handlers;
  }
}
